import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, uploadUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Camera, ChevronLeft, ChevronRight, Loader2, LogOut, Save, Trash2, UploadCloud } from "lucide-react";
import { PhotoUploadCrop } from "@/components/PhotoUploadCrop";
import ClubCombobox from "@/components/ClubCombobox";

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0"));

function parseDayMonth(value) {
    if (!value) return { day: "", month: "" };
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (iso) return { day: iso[3], month: MONTHS_SHORT[parseInt(iso[2], 10) - 1] || "" };
    const m = /^(\d{1,2})-([A-Z][a-z]{2})$/.exec(value);
    if (m) return { day: m[1].padStart(2, "0"), month: m[2] };
    return { day: "", month: "" };
}

function DayMonthPicker({ value, onChange, testid }) {
    const parsed = parseDayMonth(value);
    const [day, setDay] = useState(parsed.day);
    const [month, setMonth] = useState(parsed.month);
    const lastRef = useRef(value || "");

    useEffect(() => {
        if ((value || "") !== lastRef.current) {
            const p = parseDayMonth(value);
            setDay(p.day);
            setMonth(p.month);
            lastRef.current = value || "";
        }
    }, [value]);

    const emit = (d, mo) => {
        const next = d && mo ? `${d}-${mo}` : "";
        lastRef.current = next;
        onChange(next);
    };

    return (
        <div className="grid grid-cols-2 gap-2">
            <Select value={day || undefined} onValueChange={(d) => { setDay(d); emit(d, month); }}>
                <SelectTrigger data-testid={`${testid}-day`}><SelectValue placeholder="Day" /></SelectTrigger>
                <SelectContent className="max-h-64">
                    {DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
            </Select>
            <Select value={month || undefined} onValueChange={(mo) => { setMonth(mo); emit(day, mo); }}>
                <SelectTrigger data-testid={`${testid}-month`}><SelectValue placeholder="Month" /></SelectTrigger>
                <SelectContent>
                    {MONTHS_SHORT.map((mo) => <SelectItem key={mo} value={mo}>{mo}</SelectItem>)}
                </SelectContent>
            </Select>
        </div>
    );
}

export default function AdminEditMemberDialog({
    open,
    onOpenChange,
    member,
    onSaved,
    position = null,
    hasPrev = false,
    hasNext = false,
    onPrev,
    onNext,
}) {
    const [form, setForm] = useState({});
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState("");
    const [removePhoto, setRemovePhoto] = useState(false);
    const [spousePhotoFile, setSpousePhotoFile] = useState(null);
    const [spousePhotoPreview, setSpousePhotoPreview] = useState("");
    const [removeSpousePhoto, setRemoveSpousePhoto] = useState(false);
    const [saving, setSaving] = useState(false);
    const photoRef = useRef(null);
    const spouseRef = useRef(null);

    useEffect(() => {
        if (!member || !open) return;
        setForm({
            name: member.name || "",
            // Iter 41c: coerce any legacy canonical phone (e.g. "+919...") to the
            // last-10 national digits, matching the phone-storage policy.
            phone: String(member.phone || "").replace(/\D+/g, "").slice(-10),
            club_name: member.club_name || "",
            position: member.position || "",
            classification: member.classification || "",
            ri_district: member.ri_district || "",
            committees: member.committees || "",
            email: member.email || "",
            birthday: member.birthday || "",
            postal_address: member.postal_address || "",
            spouse_name: member.spouse_name || "",
            spouse_birthday: member.spouse_birthday || "",
            anniversary: member.anniversary || "",
            // Iter 100j · optional spouse contact fields. Excluded from
            // profile-completion, so blank rows keep their 100%.
            spouse_phone: member.spouse_phone || "",
            spouse_email: member.spouse_email || "",
        });
        setPhotoFile(null);
        setPhotoPreview("");
        setRemovePhoto(false);
        setSpousePhotoFile(null);
        setSpousePhotoPreview("");
        setRemoveSpousePhoto(false);
    }, [member, open]);

    const handleFile = (e, isSpouse) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type)) {
            toast.error("Photo must be JPG or PNG");
            e.target.value = "";
            return;
        }
        const preview = URL.createObjectURL(file);
        if (isSpouse) { setSpousePhotoFile(file); setSpousePhotoPreview(preview); setRemoveSpousePhoto(false); }
        else { setPhotoFile(file); setPhotoPreview(preview); setRemovePhoto(false); }
    };

    // Called by PhotoUploadCrop with the already-cropped 272×272 JPEG File.
    const acceptCroppedPhoto = (file, isSpouse) => {
        const preview = URL.createObjectURL(file);
        if (isSpouse) { setSpousePhotoFile(file); setSpousePhotoPreview(preview); setRemoveSpousePhoto(false); }
        else { setPhotoFile(file); setPhotoPreview(preview); setRemovePhoto(false); }
    };

    const save = async ({ advance = false } = {}) => {
        // Phone standard (iter 40): exactly 10 digits, no spaces, no CC. Reject otherwise.
        const digits = String(form.phone || "").replace(/\D+/g, "").slice(-10);
        if (digits.length !== 10) {
            toast.error(`Phone must be exactly 10 digits (no country code). Got ${digits.length}.`);
            return;
        }
        form.phone = digits;
        setSaving(true);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
            if (photoFile) fd.append("photo", photoFile);
            if (spousePhotoFile) fd.append("spouse_photo", spousePhotoFile);
            fd.append("remove_photo", removePhoto ? "true" : "false");
            fd.append("remove_spouse_photo", removeSpousePhoto ? "true" : "false");
            const { data } = await api.post(`/admin/members/${member.token}/edit`, fd);
            toast.success(advance ? "Saved. Loading next…" : "Member updated");
            onSaved?.(data, advance);
            if (!advance) {
                // Reset local file/remove flags to avoid stale re-uploads on next save
                setPhotoFile(null);
                setPhotoPreview("");
                setRemovePhoto(false);
                setSpousePhotoFile(null);
                setSpousePhotoPreview("");
                setRemoveSpousePhoto(false);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (!member) return null;

    const photoSrc = photoPreview || (removePhoto ? "" : (member.photo_filename ? uploadUrl(member.photo_filename, member.updated_at) : ""));
    const spouseSrc = spousePhotoPreview || (removeSpousePhoto ? "" : (member.spouse_photo_filename ? uploadUrl(member.spouse_photo_filename, member.updated_at) : ""));

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="admin-edit-dialog">
                <DialogHeader>
                    <DialogTitle className="rotary-heading text-2xl flex items-center justify-between gap-3">
                        <span>Edit member</span>
                        {position && (
                            <span className="label-eyebrow text-muted-foreground" data-testid="edit-position-indicator">
                                {position.current} / {position.total}
                            </span>
                        )}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6 pt-2">
                    {/* Photo — Upload + crop (1:1). Enforced 272×272 min. */}
                    <div className="space-y-1.5">
                        <span className="label-eyebrow">Member photo</span>
                        <PhotoUploadCrop
                            subject="member"
                            previewSrc={photoSrc}
                            onCropped={(file) => acceptCroppedPhoto(file, false)}
                            onRemove={photoSrc ? (() => { setPhotoFile(null); setPhotoPreview(""); setRemovePhoto(true); }) : undefined}
                            testid="edit-photo"
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <EditField label="Name" testid="edit-name">
                            <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name-input" />
                        </EditField>
                        <EditField label="Phone (10 digits)" testid="edit-phone">
                            <Input
                                value={form.phone || ""}
                                onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D+/g, "").slice(-10) })}
                                placeholder="9876543210"
                                maxLength={10}
                                inputMode="numeric"
                                data-testid="edit-phone-input"
                            />
                        </EditField>
                        <EditField label="Home Club" testid="edit-club">
                            <ClubCombobox
                                value={form.club_name || ""}
                                onChange={(name) => setForm({ ...form, club_name: name })}
                                testId="edit-club-input"
                            />
                        </EditField>
                        <EditField label="Position" testid="edit-position">
                            <Input value={form.position || ""} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="edit-position-input" />
                        </EditField>
                        <EditField label="Classification (max 70 chars)" testid="edit-classification">
                            <Input
                                value={form.classification || ""}
                                onChange={(e) => setForm({ ...form, classification: e.target.value.slice(0, 70) })}
                                maxLength={70}
                                data-testid="edit-classification-input"
                            />
                        </EditField>
                        <EditField label="RI District" testid="edit-ri-district">
                            <Input value={form.ri_district || ""} onChange={(e) => setForm({ ...form, ri_district: e.target.value })} data-testid="edit-ri-district-input" />
                        </EditField>
                        <EditField label="Committees" testid="edit-committees">
                            <Input value={form.committees || ""} onChange={(e) => setForm({ ...form, committees: e.target.value })} placeholder="Comma-separated" data-testid="edit-committees-input" />
                        </EditField>
                        <EditField label="Email" testid="edit-email">
                            <Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="edit-email-input" />
                        </EditField>
                        <EditField label="Birthday" testid="edit-birthday">
                            <DayMonthPicker value={form.birthday || ""} onChange={(v) => setForm({ ...form, birthday: v })} testid="edit-birthday" />
                        </EditField>
                        <div className="sm:col-span-2">
                            <EditField label="Communication address" testid="edit-address">
                                <Textarea rows={2} value={form.postal_address || ""} onChange={(e) => setForm({ ...form, postal_address: e.target.value })} data-testid="edit-address-input" />
                            </EditField>
                        </div>
                    </div>

                    <div className="rounded-xl border bg-muted/30 p-4 space-y-4">
                        <span className="label-eyebrow">Spouse (optional)</span>
                        <EditField label="Spouse name" testid="edit-spouse-name">
                            <Input value={form.spouse_name || ""} onChange={(e) => setForm({ ...form, spouse_name: e.target.value })} data-testid="edit-spouse-name-input" />
                        </EditField>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <EditField label="Spouse birthday" testid="edit-spouse-birthday">
                                <DayMonthPicker value={form.spouse_birthday || ""} onChange={(v) => setForm({ ...form, spouse_birthday: v })} testid="edit-spouse-birthday" />
                            </EditField>
                            <EditField label="Anniversary" testid="edit-anniversary">
                                <DayMonthPicker value={form.anniversary || ""} onChange={(v) => setForm({ ...form, anniversary: v })} testid="edit-anniversary" />
                            </EditField>
                            {/* Iter 100j · optional spouse contact fields.
                                Only show when a spouse name is set; not counted
                                in profile-completion. */}
                            {(form.spouse_name || "").trim() && (
                                <>
                                    <EditField label="Spouse phone (optional)" testid="edit-spouse-phone">
                                        <Input
                                            value={form.spouse_phone || ""}
                                            onChange={(e) => setForm({ ...form, spouse_phone: e.target.value })}
                                            inputMode="tel"
                                            maxLength={20}
                                            placeholder="e.g. 9876543210"
                                            data-testid="edit-spouse-phone-input"
                                        />
                                    </EditField>
                                    <EditField label="Spouse email (optional)" testid="edit-spouse-email">
                                        <Input
                                            type="email"
                                            value={form.spouse_email || ""}
                                            onChange={(e) => setForm({ ...form, spouse_email: e.target.value })}
                                            placeholder="spouse@example.com"
                                            data-testid="edit-spouse-email-input"
                                        />
                                    </EditField>
                                </>
                            )}
                        </div>
                        <div className="space-y-1.5">
                            <span className="label-eyebrow">Spouse photo</span>
                            <PhotoUploadCrop
                                subject="spouse"
                                previewSrc={spouseSrc}
                                onCropped={(file) => acceptCroppedPhoto(file, true)}
                                onRemove={spouseSrc ? (() => { setSpousePhotoFile(null); setSpousePhotoPreview(""); setRemoveSpousePhoto(true); }) : undefined}
                                testid="edit-spouse-photo"
                            />
                        </div>
                    </div>
                </div>

                <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-between">
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            onClick={onPrev}
                            disabled={!hasPrev || saving}
                            className="rounded-full"
                            data-testid="edit-prev-btn"
                            title="Previous member"
                        >
                            <ChevronLeft className="mr-1 h-4 w-4" /> Prev
                        </Button>
                        <Button
                            variant="outline"
                            onClick={onNext}
                            disabled={!hasNext || saving}
                            className="rounded-full"
                            data-testid="edit-next-btn"
                            title="Next member (discards unsaved changes)"
                        >
                            Next <ChevronRight className="ml-1 h-4 w-4" />
                        </Button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="edit-exit-btn">
                            <LogOut className="mr-1.5 h-4 w-4" /> Exit
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => save({ advance: false })}
                            disabled={saving}
                            className="rounded-full border-rotary-blue text-rotary-blue hover:bg-rotary-blue hover:text-white"
                            data-testid="edit-save-btn"
                        >
                            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
                            Save
                        </Button>
                        <Button
                            onClick={() => save({ advance: true })}
                            disabled={saving || !hasNext}
                            className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky"
                            data-testid="edit-save-next-btn"
                            title={hasNext ? "Save and load next member" : "No next member"}
                        >
                            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
                            Save &amp; Next <ChevronRight className="ml-1 h-4 w-4" />
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function EditField({ label, testid, children }) {
    return (
        <div data-testid={testid}>
            <Label className="label-eyebrow">{label}</Label>
            <div className="mt-2">{children}</div>
        </div>
    );
}

function EditPhotoRow({ label, src, onChoose, onRemove, inputRef, onFile, testid }) {
    return (
        <div className="flex items-center gap-4">
            <div className="h-24 w-24 shrink-0 rounded-2xl border bg-muted/40 overflow-hidden grid place-items-center" data-testid={`${testid}-preview`}>
                {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : <Camera className="h-6 w-6 text-muted-foreground" />}
            </div>
            <div className="flex flex-col gap-2">
                <span className="label-eyebrow">{label}</span>
                <input ref={inputRef} type="file" accept="image/jpeg,image/jpg,image/png" onChange={onFile} className="hidden" data-testid={`${testid}-input`} />
                <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={onChoose} className="rounded-full" data-testid={`${testid}-choose-btn`}>
                        <UploadCloud className="mr-1.5 h-4 w-4" /> {src ? "Replace" : "Upload"}
                    </Button>
                    {src && (
                        <Button type="button" variant="outline" size="sm" onClick={onRemove} className="rounded-full text-rotary-cranberry border-rotary-cranberry/40 hover:bg-rotary-cranberry hover:text-white" data-testid={`${testid}-remove-btn`}>
                            <Trash2 className="mr-1.5 h-4 w-4" /> Remove
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
